import { formatMoney } from './moneyFormat'

const ERROR_MESSAGES = Object.freeze({
  access_denied: 'Недостаточно прав для работы со складом',
  archived_reference: 'Выбрана архивная запись справочника',
  duplicate_code: 'Код уже используется',
  duplicate_name: 'Название уже используется',
  insufficient_stock: 'Недостаточно остатков на выбранном складе',
  invalid_org: 'Организация не найдена',
  not_found: 'Запись не найдена',
  stock_rebuild_failed: 'Не удалось обновить складские остатки',
  validation_error: 'Проверьте заполнение формы'
})

export const balanceColumns = Object.freeze([
  { name: 'warehouse', align: 'left', label: 'Склад', field: (row) => row.warehouse && row.warehouse.name ? row.warehouse.name : row.warehouse_name || row.warehouse_id || '', required: true },
  { name: 'code', align: 'left', label: 'Код', field: 'code' },
  { name: 'name', align: 'left', label: 'Номенклатура', field: 'name', required: true },
  { name: 'unit_name', align: 'left', label: 'Ед.', field: 'unit_name' },
  { name: 'quantity', align: 'right', label: 'Количество', field: 'quantity', required: true },
  { name: 'average_cost', align: 'right', label: 'Средняя цена', field: 'average_cost', format: (value) => formatMoney(value) },
  { name: 'total_cost', align: 'right', label: 'Сумма', field: 'total_cost', format: (value) => formatMoney(value) }
])

export const movementColumns = Object.freeze([
  { name: 'date', align: 'left', label: 'Дата', field: 'date', required: true },
  { name: 'operation_label', align: 'left', label: 'Операция', field: 'operation_label', required: true },
  { name: 'warehouse', align: 'left', label: 'Склад', field: (row) => row.warehouse && row.warehouse.name ? row.warehouse.name : row.warehouse_name || row.warehouse_id || '' },
  { name: 'name', align: 'left', label: 'Номенклатура', field: 'name', required: true },
  { name: 'incoming_quantity', align: 'right', label: 'Приход', field: 'incoming_quantity' },
  { name: 'outgoing_quantity', align: 'right', label: 'Расход', field: 'outgoing_quantity' },
  { name: 'remaining_balance', align: 'right', label: 'Остаток', field: 'remaining_balance' },
  { name: 'document', align: 'left', label: 'Источник', field: 'document' }
])

export const stockCardColumns = Object.freeze([
  { name: 'date', align: 'left', label: 'Дата', field: 'date' },
  { name: 'operation_label', align: 'left', label: 'Операция', field: 'operation_label' },
  { name: 'document', align: 'left', label: 'Документ', field: 'document' },
  { name: 'incoming_quantity', align: 'right', label: 'Приход', field: 'incoming_quantity' },
  { name: 'outgoing_quantity', align: 'right', label: 'Расход', field: 'outgoing_quantity' },
  { name: 'remaining_balance', align: 'right', label: 'Остаток', field: 'remaining_balance' }
])

export const warehouseColumns = Object.freeze([
  { name: 'code', align: 'left', label: 'Код', field: 'code', required: true },
  { name: 'name', align: 'left', label: 'Название', field: 'name', required: true },
  { name: 'description', align: 'left', label: 'Описание', field: 'description' },
  { name: 'is_active', align: 'left', label: 'Статус', field: 'is_active' },
  { name: 'actions', align: 'right', label: 'Действия', field: 'id', required: true }
])

export const movementOperationOptions = Object.freeze([
  { label: 'Все операции', value: 'all' },
  { label: 'Поступление', value: 'receipt' },
  { label: 'Ввод остатков', value: 'opening_balance' },
  { label: 'Перемещение', value: 'warehouse_transfer' },
  { label: 'Корректировка', value: 'inventory_adjustment' },
  { label: 'Списание', value: 'write_off' }
])

export const warehouseDocumentRoutes = Object.freeze({
  upd: 'document_info_upd',
  sale: 'document_info_upd',
  purchase: 'document_info_upd',
  payment: 'document_info_payment',
  avance: 'document_info_factura_avance',
  factura_avance: 'document_info_factura_avance',
  accept_factura_avance: 'document_info_factura_avance',
  bill_to_pay: 'document_info_bill_to_pay',
  act_sver: 'document_info_act_sver'
})

function compactFilters(filters = {}) {
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '' && value !== null && value !== undefined))
}

function errorDetails(response = {}) {
  if (response.details && typeof response.details === 'object') return response.details
  if (response.error_info && response.error_info.details && typeof response.error_info.details === 'object') return response.error_info.details
  return {}
}

export function warehouseError(response = {}, fallback = 'Не удалось загрузить складские данные') {
  const details = errorDetails(response)
  const code = details.code || (response.error_info && response.error_info.code) || response.error
  const message = ERROR_MESSAGES[code] || response.message || fallback
  const detailParts = []
  const position = details.position || details.item || details.nomenclature_name
  const warehouse = details.warehouse || details.warehouse_name
  if (position) detailParts.push(`Позиция: ${position}`)
  if (warehouse) detailParts.push(`Склад: ${warehouse}`)
  if (details.requested_quantity !== undefined || details.available_quantity !== undefined) {
    detailParts.push(`Запрошено: ${details.requested_quantity || 0}, доступно: ${details.available_quantity || 0}`)
  }
  return detailParts.length ? `${message}. ${detailParts.join('. ')}` : message
}

export function isWarehouseError(response = {}) {
  return response.type === 'error' || Boolean(response.error)
}

export function normalizeDiagnostics(response = {}) {
  return (Array.isArray(response.diagnostics) ? response.diagnostics : []).map((diagnostic, index) => ({
    key: diagnostic.code || `diagnostic-${index}`,
    level: diagnostic.level || 'info',
    message: diagnostic.message || diagnostic.code || 'Диагностика склада без описания'
  }))
}

export function normalizeBalanceRows(response = {}) {
  return (Array.isArray(response.balances) ? response.balances : []).map((row, index) => ({
    key: `${row.warehouse_id || 'none'}-${row.nomenclature_id || row.name || index}`,
    id: `${row.warehouse_id || 'none'}-${row.nomenclature_id || row.name || index}`,
    ...row
  }))
}

export function normalizeMovementRows(response = {}) {
  return (Array.isArray(response.movements) ? response.movements : []).map((row, index) => ({
    key: `${row.transfer_id || row.document_id || index}-${index}`,
    id: `${row.transfer_id || row.document_id || index}-${index}`,
    ...row
  }))
}

export function normalizeWarehouseRows(response = {}) {
  return Array.isArray(response.warehouses) ? response.warehouses : []
}

export function normalizeNomenclatureRows(response = {}) {
  return response.nomenclature || response.abstract_positions || []
}

export function warehouseOption(row = {}) {
  return { label: `${row.code || row.id} — ${row.name || 'Без названия'}`, value: row.id }
}

export function nomenclatureOption(row = {}) {
  return { label: `${row.code || row.id || row.primary} — ${row.common_name || row.name || 'Без названия'}`, value: row.id || row.primary }
}

export function createWarehouseBrowseAdapter(connection) {
  const request = (prefix, send) => {
    const requestId = connection.makeRequestId(prefix)
    send(requestId)
    return requestId
  }

  return {
    balances(filters) {
      return request('warehouse-browse-balances', (requestId) => connection.getStockBalances(requestId, compactFilters(filters)))
    },
    movements(filters) {
      return request('warehouse-browse-movements', (requestId) => connection.getStockMovements(requestId, compactFilters(filters)))
    },
    stockCard(filters) {
      return request('warehouse-browse-card', (requestId) => connection.getStockCard(requestId, compactFilters(filters)))
    },
    warehouses(includeArchived = true) {
      return request('warehouse-browse-warehouses', (requestId) => connection.listWarehouses(requestId, { include_archived: includeArchived }))
    },
    nomenclature(includeArchived = false) {
      return request('warehouse-browse-nomenclature', (requestId) => connection.listNomenclature(requestId, { include_archived: includeArchived }))
    },
    createWarehouse(payload) {
      return request('warehouse-directory-create', (requestId) => connection.createWarehouse(requestId, payload))
    },
    updateWarehouse(warehouseId, payload) {
      return request('warehouse-directory-update', (requestId) => connection.updateWarehouse(requestId, warehouseId, payload))
    },
    archiveWarehouse(warehouseId) {
      return request('warehouse-directory-archive', (requestId) => connection.archiveWarehouse(requestId, warehouseId))
    }
  }
}
